import React from 'react';
import { Modal, Descriptions, Progress, Tag, Card, Space, Row, Col, Divider, Typography, Alert, Table } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  WarningOutlined,
  TrophyOutlined,
  BookOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

interface ScreeningDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  screeningDetails: any;
  candidateName: string;
}

const ScreeningDetailsModal: React.FC<ScreeningDetailsModalProps> = ({
  visible,
  onClose,
  screeningDetails,
  candidateName
}) => {
  if (!screeningDetails) return null;

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#52c41a'; // Green
    if (score >= 60) return '#faad14'; // Orange
    return '#ff4d4f'; // Red
  };

  const getScoreStatus = (score: number): 'success' | 'normal' | 'exception' => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'normal';
    return 'exception';
  };

  return (
    <Modal
      title={
        <div style={{ fontSize: '22px', fontWeight: 'bold' }}>
          🎯 Screening Analysis: {candidateName}
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width={900}
      footer={null}
      style={{ top: 20 }}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '10px' }}>
        {/* Overall Score */}
        <Card 
          style={{ 
            marginBottom: '20px', 
            background: `linear-gradient(135deg, ${getScoreColor(screeningDetails.overallScore)}15 0%, white 100%)`,
            border: `2px solid ${getScoreColor(screeningDetails.overallScore)}`
          }}
        >
          <Row gutter={16} align="middle">
            <Col span={12}>
              <Title level={4} style={{ margin: 0 }}>Overall Match Score</Title>
              <Title level={1} style={{ 
                margin: 0, 
                color: getScoreColor(screeningDetails.overallScore),
                fontSize: '48px'
              }}>
                {screeningDetails.overallScore}%
              </Title>
            </Col>
            <Col span={12}>
              <Progress
                type="dashboard"
                percent={screeningDetails.overallScore}
                status={getScoreStatus(screeningDetails.overallScore)}
                strokeColor={getScoreColor(screeningDetails.overallScore)}
                width={120}
              />
            </Col>
          </Row>
        </Card>

        {/* Score Breakdown */}
        <Card title={<Text strong style={{ fontSize: '16px' }}>📊 Score Breakdown</Text>} style={{ marginBottom: '20px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text><ToolOutlined /> Skills Match</Text>
                  <Text strong style={{ color: getScoreColor(screeningDetails.skillsMatch) }}>
                    {screeningDetails.skillsMatch}%
                  </Text>
                </div>
                <Progress 
                  percent={screeningDetails.skillsMatch} 
                  strokeColor={getScoreColor(screeningDetails.skillsMatch)}
                  showInfo={false}
                />
              </div>
            </Col>
            
            <Col xs={24} md={12}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text><TrophyOutlined /> Experience Match</Text>
                  <Text strong style={{ color: getScoreColor(screeningDetails.experienceMatch) }}>
                    {screeningDetails.experienceMatch}%
                  </Text>
                </div>
                <Progress 
                  percent={screeningDetails.experienceMatch} 
                  strokeColor={getScoreColor(screeningDetails.experienceMatch)}
                  showInfo={false}
                />
              </div>
            </Col>
            
            <Col xs={24} md={12}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text><EnvironmentOutlined /> Location Match</Text>
                  <Text strong style={{ color: getScoreColor(screeningDetails.locationMatch) }}>
                    {screeningDetails.locationMatch}%
                  </Text>
                </div>
                <Progress 
                  percent={screeningDetails.locationMatch} 
                  strokeColor={getScoreColor(screeningDetails.locationMatch)}
                  showInfo={false}
                />
              </div>
            </Col>
            
            <Col xs={24} md={12}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text><BookOutlined /> Education Match</Text>
                  <Text strong style={{ color: getScoreColor(screeningDetails.educationMatch) }}>
                    {screeningDetails.educationMatch}%
                  </Text>
                </div>
                <Progress 
                  percent={screeningDetails.educationMatch} 
                  strokeColor={getScoreColor(screeningDetails.educationMatch)}
                  showInfo={false}
                />
              </div>
            </Col>
          </Row>
        </Card>

        {/* Extracted Information */}
        <Card title={<Text strong style={{ fontSize: '16px' }}>📋 Candidate Analysis</Text>} style={{ marginBottom: '20px' }}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label={<Text strong>Experience</Text>}>
              {screeningDetails.analysisDetails?.extractedExperience || 'Not specified'}
            </Descriptions.Item>
            <Descriptions.Item label={<Text strong>Education</Text>}>
              {screeningDetails.analysisDetails?.extractedEducation || 'Not specified'}
            </Descriptions.Item>
            <Descriptions.Item label={<Text strong>Location</Text>}>
              {screeningDetails.analysisDetails?.extractedLocation || 'Not specified'}
              {screeningDetails.analysisDetails?.extractedRelocation && (
                <Tag color="blue" style={{ marginLeft: '8px' }}>
                  Relocation: {screeningDetails.analysisDetails.extractedRelocation}
                </Tag>
              )}
            </Descriptions.Item>
            {screeningDetails.analysisDetails?.extractedJobPreference && (
              <Descriptions.Item label={<Text strong>Job Preference</Text>}>
                {screeningDetails.analysisDetails.extractedJobPreference}
                {screeningDetails.analysisDetails?.jobWorkType && (
                  <Tag color="cyan" style={{ marginLeft: '8px' }}>
                    Job Type: {screeningDetails.analysisDetails.jobWorkType}
                  </Tag>
                )}
              </Descriptions.Item>
            )}
            {screeningDetails.analysisDetails?.profileStrength && (
              <Descriptions.Item label={<Text strong>Profile Strength</Text>}>
                <Progress 
                  percent={screeningDetails.analysisDetails.profileStrength} 
                  size="small"
                  style={{ width: '200px' }}
                />
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Skills Analysis */}
        <Card title={<Text strong style={{ fontSize: '16px' }}>🛠️ Skills Analysis</Text>} style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Text strong style={{ fontSize: '14px' }}>Matching Skills ({screeningDetails.keywordMatches?.length || 0})</Text>
            <div style={{ marginTop: '8px' }}>
              {screeningDetails.keywordMatches && screeningDetails.keywordMatches.length > 0 ? (
                screeningDetails.keywordMatches.map((skill: string) => (
                  <Tag key={skill} color="green" icon={<CheckCircleOutlined />} style={{ marginBottom: '8px' }}>
                    {skill}
                  </Tag>
                ))
              ) : (
                <Text type="secondary">No matching skills found</Text>
              )}
            </div>
          </div>
          
          {screeningDetails.missingSkills && screeningDetails.missingSkills.length > 0 && (
            <div>
              <Text strong style={{ fontSize: '14px' }}>Missing Skills ({screeningDetails.missingSkills.length})</Text>
              <div style={{ marginTop: '8px' }}>
                {screeningDetails.missingSkills.map((skill: string) => (
                  <Tag key={skill} color="red" icon={<CloseCircleOutlined />} style={{ marginBottom: '8px' }}>
                    {skill}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {screeningDetails.analysisDetails?.extractedSkills && screeningDetails.analysisDetails.extractedSkills.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <Text strong style={{ fontSize: '14px' }}>All Extracted Skills ({screeningDetails.analysisDetails.extractedSkills.length})</Text>
              <div style={{ marginTop: '8px' }}>
                {screeningDetails.analysisDetails.extractedSkills.map((skill: string) => (
                  <Tag key={skill} color="blue" style={{ marginBottom: '8px' }}>
                    {skill}
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Recommendations */}
        {screeningDetails.recommendations && screeningDetails.recommendations.length > 0 && (
          <Card title={<Text strong style={{ fontSize: '16px' }}>💡 Recommendations</Text>}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {screeningDetails.recommendations.map((rec: string, index: number) => {
                const isPositive = rec.includes('✅') || rec.includes('Strong');
                const isWarning = rec.includes('⚠️') || rec.includes('Below');
                const isDanger = rec.includes('❌') || rec.includes('AUTO-REJECTED');
                
                return (
                  <Alert
                    key={index}
                    message={rec}
                    type={isDanger ? 'error' : isWarning ? 'warning' : isPositive ? 'success' : 'info'}
                    icon={
                      isDanger ? <CloseCircleOutlined /> : 
                      isWarning ? <WarningOutlined /> : 
                      isPositive ? <CheckCircleOutlined /> : null
                    }
                    showIcon
                  />
                );
              })}
            </Space>
          </Card>
        )}

        {/* Pre-Screening Questions & Answers */}
        {screeningDetails.preScreeningAnswers && screeningDetails.preScreeningAnswers.length > 0 && (
          <Card 
            title={
              <Text strong style={{ fontSize: '16px' }}>
                <QuestionCircleOutlined style={{ marginRight: '8px' }} />
                Pre-Screening Questions & Answers
              </Text>
            } 
            style={{ marginTop: '20px' }}
          >
            <Table
              dataSource={screeningDetails.preScreeningAnswers.map((item: any, idx: number) => ({
                key: idx,
                question: item.question,
                answer: item.answer,
              }))}
              columns={[
                {
                  title: 'Question',
                  dataIndex: 'question',
                  key: 'question',
                  width: '50%',
                  render: (text: string) => <Text strong>{text}</Text>,
                },
                {
                  title: 'Answer',
                  dataIndex: 'answer',
                  key: 'answer',
                  width: '50%',
                  render: (text: string) => {
                    // Color-code certain answers
                    const lowerAnswer = text.toLowerCase();
                    if (lowerAnswer === 'yes' || lowerAnswer.includes('willing') || lowerAnswer.includes('open')) {
                      return <Tag color="green">{text}</Tag>;
                    }
                    if (lowerAnswer === 'no' || lowerAnswer.includes('not willing') || lowerAnswer.includes('not open')) {
                      return <Tag color="red">{text}</Tag>;
                    }
                    return <Text>{text}</Text>;
                  },
                },
              ]}
              pagination={false}
              size="small"
              bordered
            />
          </Card>
        )}

        {/* Work Experience Details */}
        {(screeningDetails.analysisDetails?.extractedRoles || screeningDetails.analysisDetails?.extractedCompanies) && (
          <Card title={<Text strong style={{ fontSize: '16px' }}>💼 Work History</Text>} style={{ marginTop: '20px' }}>
            {screeningDetails.analysisDetails.extractedRoles && screeningDetails.analysisDetails.extractedRoles.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <Text strong>Roles: </Text>
                {screeningDetails.analysisDetails.extractedRoles.map((role: string, idx: number) => (
                  <Tag key={idx} color="purple" style={{ marginTop: '4px' }}>{role}</Tag>
                ))}
              </div>
            )}
            {screeningDetails.analysisDetails.extractedCompanies && screeningDetails.analysisDetails.extractedCompanies.length > 0 && (
              <div>
                <Text strong>Companies: </Text>
                {screeningDetails.analysisDetails.extractedCompanies.map((company: string, idx: number) => (
                  <Tag key={idx} color="orange" style={{ marginTop: '4px' }}>{company}</Tag>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </Modal>
  );
};

export default ScreeningDetailsModal;

